import React, { useMemo, useState } from 'react';
import { View, ScrollView, StyleSheet, NativeSyntheticEvent, NativeScrollEvent, LayoutChangeEvent, ViewStyle } from 'react-native';
import { Colors } from '@/constants/colors';

interface ScrollColumnProps {
  children: React.ReactNode;
  style?: ViewStyle;
  contentContainerStyle?: ViewStyle;
}

/**
 * ScrollColumn
 * A ScrollView with a custom, aesthetically pleasing vertical scrollbar overlay.
 * Works on native and web. Automatically appears only when content overflows.
 */
export const ScrollColumn: React.FC<ScrollColumnProps> = ({ children, style, contentContainerStyle }) => {
  const [containerHeight, setContainerHeight] = useState(1);
  const [contentHeight, setContentHeight] = useState(1);
  const [scrollY, setScrollY] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => {
    setContainerHeight(Math.max(1, e.nativeEvent.layout.height));
  };

  const onContentSizeChange = (_w: number, h: number) => {
    setContentHeight(Math.max(1, h));
  };

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    setScrollY(e.nativeEvent.contentOffset.y);
  };

  const { showBar, trackHeight, thumbHeight, thumbTop } = useMemo(() => {
    const padding = 8; // visual insets inside the column
    const trackHeight = Math.max(0, containerHeight - padding * 2);
    const showBar = contentHeight > containerHeight + 1;
    const visibleRatio = Math.min(1, containerHeight / contentHeight);
    const minThumb = 24;
    const thumbHeight = Math.max(minThumb, trackHeight * visibleRatio);
    const maxScroll = Math.max(1, contentHeight - containerHeight);
    const scrollRatio = Math.min(1, Math.max(0, scrollY / maxScroll));
    const thumbTop = padding + (trackHeight - thumbHeight) * scrollRatio;
    return { showBar, trackHeight, thumbHeight, thumbTop };
  }, [containerHeight, contentHeight, scrollY]);

  return (
    <View style={[styles.container, style]} onLayout={onLayout}>
      <ScrollView
        onScroll={onScroll}
        scrollEventThrottle={16}
        onContentSizeChange={onContentSizeChange}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={contentContainerStyle}
      >
        {children}
      </ScrollView>
      {showBar && (
        <View pointerEvents="none" style={styles.scrollbarContainer}>
          <View style={styles.track} />
          <View style={[styles.thumb, { height: thumbHeight, top: thumbTop }]} />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    flex: 1,
  },
  scrollbarContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 6,
    width: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  track: {
    position: 'absolute',
    top: 8,
    bottom: 8,
    width: 4,
    borderRadius: 2,
    backgroundColor: Colors.border.subtle,
    opacity: 0.4,
  },
  thumb: {
    position: 'absolute',
    right: 2,
    width: 6,
    borderRadius: 3,
    backgroundColor: Colors.accent.primary,
    opacity: 0.8,
  },
});


